import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useObservable } from 'react-use-observable';

import { Typography } from 'antd';

import { useQuery } from '@tanstack/react-query';
import { MODAL_NEWS_RVP_ENABLED } from 'settings';
import EliteCard from 'v2/components/Pages/Dashboard/EliteCard';
import LineChart from 'v2/components/Pages/Dashboard/LineChart';
import { steps } from 'v2/components/Pages/Dashboard/tour';
import Banner from 'v2/components/Shared/Banner';
import BlacklistMessage from 'v2/components/Shared/BlacklistMessage';
import ChargebackAlert from 'v2/components/Shared/ChargebackAlert';
import ModalBelt from 'v2/components/Shared/ModalBelt';
import ModalFidc from 'v2/components/Shared/ModalFidc';
import { ModalNews } from 'v2/components/Shared/ModalNews';
import { ModalNewsRVP } from 'v2/components/Shared/ModalNewsRVP';
import ModalOwnership from 'v2/components/Shared/ModalOwnership';
import { ModalTerms } from 'v2/components/Shared/ModalTerms';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import Tour from 'v2/components/Shared/Tour';
import Transfer from 'v2/components/Shared/Transfer';
import { ToggleButton } from 'v2/components/Shared/ValuesVisibility';
import apiService from 'v2/services/api';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { useUserStore } from 'v2/store/User';

import ActivationCampaign from './activationCampaign';
import AccountManager from './components/AccountManager';
import BarChart from './components/BarChart';
import CardTransferPJ from './components/CardTransferPJ';
import DoughnutChart from './components/DoughnutChart';
import ImpactMeter from './components/ImpactMeter';
import DiplomaBanner from './components/ImpactMeter/DiplomaBanner';
import News from './components/News';
import { QualificationValidation } from './components/QualificationValidation';
import SolucxNps from './components/SolucxNps';
import Wallet from './components/Wallet';

const CONSULTANCY_ACCOUNT_MANAGER_IDS = [792, 2073, 2309, 363];
const IMPACTOMETER_BETA = 'impactometro';

function DashboardFC() {
  const history = useHistory();

  const { user, originalUser } = useUserStore(({ user, originalUser }) => ({ user, originalUser }));

  useEffect(() => {
    if (user?.acceptence_terms && !user?.producer && !user?.coproducer && !user?.affiliate) {
      history.push('/producer/quickstart');
    }
  }, [user, history]);

  const hasPublicManager = user?.account_manager?.isPublicManager;
  const hasConsultancy = CONSULTANCY_ACCOUNT_MANAGER_IDS.includes(user?.account_manager_id);
  const userName = (user?.business_name || user?.full_name || '').split(' ')[0];

  const [betaModules] = useObservable(() => eduzzAccountsService.getBetaModules(), []);
  const hasImpactometerBeta = !!betaModules?.includes(IMPACTOMETER_BETA);

  return (
    <>
      <div className='w-full'>
        {!!originalUser && !originalUser.is_support && <SolucxNps />}
        <UserGreetings />

        <PermissionHide action='myeduzz:dashboard:visualizar' fallback={<News />}>
          <div className='mt-4 flex flex-wrap items-center justify-between gap-4 md:flex-nowrap'>
            <Typography.Title level={3} className='m-0'>
              Bem-vindo(a) de volta{userName ? `, ${userName}` : ''}!
            </Typography.Title>
            <ToggleButton />
          </div>

          {hasImpactometerBeta && <ImpactMeter />}

          <div className='mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
            <LineChart />

            <EliteCard />

            <div className='flex size-full flex-col'>
              {(hasPublicManager || hasConsultancy) && (
                <AccountManager hasPublicManager={hasPublicManager} hasConsultancy={hasConsultancy} />
              )}

              {originalUser?.show_legal_person_card && originalUser?.type === 'J' && !originalUser?.crossborder ? (
                <CardTransferPJ user={originalUser} />
              ) : originalUser?.crossborder ? (
                <Transfer />
              ) : (
                <Wallet />
              )}
            </div>

            <DoughnutChart id='best-sellers-chart' />

            <BarChart id='recent-earnings-chart' />

            <News />
          </div>

          {hasImpactometerBeta && <DiplomaBanner />}
        </PermissionHide>
      </div>

      <QualificationValidation />
      <ActivationCampaign userId={user?.id} />
    </>
  );
}

const UserGreetings: React.FC = () => {
  const { data: productsWithoutRVP, isLoading: loadingModalNewsRVP } = useQuery<{ exists: boolean }>({
    queryKey: ['productsWithoutRvpExists'],
    queryFn: () =>
      apiService
        .get('/products-without-rvp/exists')
        .toPromise()
        .then(res => res.data),
    gcTime: 0,
    enabled: MODAL_NEWS_RVP_ENABLED
  });

  const shouldDisableModalNews = MODAL_NEWS_RVP_ENABLED && (loadingModalNewsRVP || !!productsWithoutRVP?.exists);

  return (
    <>
      <BlacklistMessage />
      <ModalTerms />
      <Tour steps={steps} name='first_tour' />
      <ModalNews disabled={shouldDisableModalNews} />
      <ModalNewsRVP available={productsWithoutRVP?.exists} disabled={!MODAL_NEWS_RVP_ENABLED} />
      <ModalOwnership />
      <ModalFidc />
      <ModalBelt />
      <ChargebackAlert />
      <Banner />
    </>
  );
};

export default DashboardFC;
